import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSupabase } from "../SupabaseContext";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "./ui/card";
import { Button } from "./ui/button";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { ScrollArea } from "./ui/scroll-area";
import { Checkbox } from "./ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import {
  Users,
  Plus,
  ArrowLeft,
  MoreVertical,
  Edit,
  Key,
  Eye,
  EyeOff,
  Building2,
} from "lucide-react";
import { useToast } from "./ui/use-toast";

// Enhanced Form Component with Password Management
const ManagerForm = ({ onSubmit, initialData = null, facilities }) => {
  const [formState, setFormState] = useState({
    first_name: initialData?.first_name || "",
    last_name: initialData?.last_name || "",
    email: initialData?.email || "",
    id_code: initialData?.id_code || "",
    selectedFacilities:
      initialData?.facility_managers?.map((fm) => fm.facility_id) || [],
  });
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [formError, setFormError] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!initialData && !formState.id_code) {
      generateRandomPassword();
    }
  }, [initialData]);

  const generateRandomPassword = () => {
    const password = `MGR${Math.random()
      .toString(36)
      .substr(2, 6)
      .toUpperCase()}`;
    setFormState((prev) => ({ ...prev, id_code: password }));
    setGeneratedPassword(password);
  };

  const handleSubmit = () => {
    const validationError = validateForm();
    if (validationError) {
      // Show toast message
      toast({
        title: "Chyba validácie",
        description: validationError,
        variant: "destructive",
      });
      return;
    }
    onSubmit(formState);
  };

  const validateForm = () => {
    if (!formState.first_name.trim()) return "Meno je povinné";
    if (!formState.last_name.trim()) return "Priezvisko je povinné";
    if (!formState.email.trim()) return "Email je povinný";
    if (!formState.id_code.trim()) return "Heslo je povinné";
    if (!formState.email.includes("@")) return "Neplatný formát emailu";
    if (formState.selectedFacilities.length === 0)
      return "Vyberte aspoň jednu skupinu";
    return null; // No errors
  };

  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
      <div>
        <Label htmlFor="first_name">Meno</Label>
        <Input
          id="first_name"
          value={formState.first_name}
          onChange={(e) =>
            setFormState((prev) => ({ ...prev, first_name: e.target.value }))
          }
        />
      </div>
      <div>
        <Label htmlFor="last_name">Priezvisko</Label>
        <Input
          id="last_name"
          value={formState.last_name}
          onChange={(e) =>
            setFormState((prev) => ({ ...prev, last_name: e.target.value }))
          }
        />
      </div>
      <div>
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formState.email}
          onChange={(e) =>
            setFormState((prev) => ({ ...prev, email: e.target.value }))
          }
        />
      </div>
      <div>
        <Label htmlFor="password">Heslo</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            value={formState.id_code}
            onChange={(e) =>
              setFormState((prev) => ({ ...prev, id_code: e.target.value }))
            }
            className="pr-20"
          />
          <div className="flex absolute top-0 right-0 items-center pr-2 space-x-1 h-full">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="w-8 h-8"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="w-8 h-8"
              onClick={generateRandomPassword}
            >
              <Key className="w-4 h-4" />
            </Button>
          </div>
        </div>
        {generatedPassword && !initialData && (
          <p className="mt-1 text-sm text-muted-foreground">
            Vygenerované heslo: {generatedPassword}
          </p>
        )}
      </div>
      <div>
        <Label>Skupiny</Label>
        <ScrollArea className="h-[200px] border rounded-md p-4">
          {facilities.map((facility) => (
            <div key={facility.id} className="flex items-center py-2 space-x-2">
              <Checkbox
                id={facility.id}
                checked={formState.selectedFacilities.includes(facility.id)}
                onCheckedChange={(checked) => {
                  setFormState((prev) => ({
                    ...prev,
                    selectedFacilities: checked
                      ? [...prev.selectedFacilities, facility.id]
                      : prev.selectedFacilities.filter(
                          (id) => id !== facility.id
                        ),
                  }));
                }}
              />
              <Label htmlFor={facility.id} className="flex items-center">
                <Building2 className="mr-2 w-4 h-4 text-gray-500" />
                {facility.name}
              </Label>
            </div>
          ))}
        </ScrollArea>
      </div>
      <DialogFooter>
        <Button onClick={handleSubmit}>
          {initialData ? "Uložiť zmeny" : "Pridať manažéra"}
        </Button>
      </DialogFooter>
    </div>
  );
};

const PasswordChangeDialog = ({ isOpen, onClose, onSubmit, managerName }) => {
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleGeneratePassword = () => {
    const password = `MGR${Math.random()
      .toString(36)
      .substr(2, 6)
      .toUpperCase()}`;
    setNewPassword(password);
  };

  const handleSubmit = () => {
    onSubmit(newPassword);
    setNewPassword("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Zmena hesla - {managerName}</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label>Nové heslo</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pr-20"
              />
              <div className="flex absolute top-0 right-0 items-center pr-2 space-x-1 h-full">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8"
                  onClick={handleGeneratePassword}
                >
                  <Key className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Zrušiť
          </Button>
          <Button onClick={handleSubmit} disabled={!newPassword}>
            Zmeniť heslo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ManagerManagement = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const supabase = useSupabase();
  const [managers, setManagers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddingManager, setIsAddingManager] = useState(false);
  const [isEditingManager, setIsEditingManager] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [error, setError] = useState(null);
  const [isPasswordChangeDialogOpen, setIsPasswordChangeDialogOpen] =
    useState(false);
  const [selectedManagerForPassword, setSelectedManagerForPassword] =
    useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      const [companyData, facilitiesData, managersData] = await Promise.all([
        supabase.from("companies").select("*").eq("id", companyId).single(),
        supabase.from("facilities").select("*").eq("company_id", companyId),
        supabase
          .from("users")
          .select(
            `
            id,
            first_name,
            last_name,
            email,
            id_code,
            facility_managers (
              facility_id,
              facilities (
                id,
                name
              )
            )
          `
          )
          .eq("company_id", companyId)
          .eq("is_manager", true) // Add this filter to get only managers
          .eq("is_active", true), // Also get only active managers
      ]);

      if (companyData.error) throw companyData.error;
      if (facilitiesData.error) throw facilitiesData.error;
      if (managersData.error) throw managersData.error;

      setCompany(companyData.data);
      setFacilities(facilitiesData.data);
      setManagers(managersData.data);
    } catch (error) {
      console.error("Error:", error);
      setError("Nepodarilo sa načítať údaje");
    } finally {
      setLoading(false);
    }
  };

  // Funkcia na aktualizáciu hesla cez API endpoint
  const updatePasswordViaAPI = async (userId, newPassword) => {
    try {
      const response = await fetch("/api/update-user-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userId,
          newPassword: newPassword,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to update password");
      }

      return result;
    } catch (error) {
      console.error("API call error:", error);
      throw error;
    }
  };

  // Vytvorí manažéra aj so Supabase Auth účtom cez serverless API
  const createManagerViaAPI = async (formData) => {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) throw sessionError;
    if (!session?.access_token) {
      throw new Error(
        "Nie ste prihlásený. Skúste sa odhlásiť a prihlásiť znova."
      );
    }

    const response = await fetch("/api/create-manager", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        companyId,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.id_code,
        selectedFacilities: formData.selectedFacilities,
      }),
    });

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || "Nepodarilo sa vytvoriť manažéra");
    }
    return result;
  };

  const handlePasswordChange = async (newPassword) => {
    if (!selectedManagerForPassword) return;

    try {
      // Použijeme API endpoint na aktualizáciu hesla v auth.users aj public.users
      await updatePasswordViaAPI(selectedManagerForPassword.id, newPassword);

      // Update local state
      setManagers(
        managers.map((manager) =>
          manager.id === selectedManagerForPassword.id
            ? { ...manager, id_code: newPassword }
            : manager
        )
      );

      toast({
        title: "Heslo úspešne zmenené",
        description: "Heslo bolo úspešne aktualizované v auth systéme.",
      });

      setSelectedManagerForPassword(null);
    } catch (err) {
      setError(`Nepodarilo sa zmeniť heslo: ${err.message}`);
      console.error("Password change error:", err);
    }
  };

  // Rest of the component remains largely the same, but add password change option to dropdown menu
  const renderManagerActions = (manager) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="p-0 w-8 h-8">
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem
          onClick={() => {
            setSelectedManager(manager);
            setIsEditingManager(true);
          }}
        >
          <Edit className="mr-2 w-4 h-4" />
          Upraviť
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setSelectedManagerForPassword(manager);
            setIsPasswordChangeDialogOpen(true);
          }}
        >
          <Key className="mr-2 w-4 h-4" />
          Zmeniť heslo
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="container p-6 mx-auto max-w-4xl">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl font-bold">
                Manažéri - {company?.name}
              </CardTitle>
              <CardDescription>
                Správa manažérov pre skupny spoločnosti
              </CardDescription>
            </div>
            <div className="flex space-x-4">
              <Button variant="outline" onClick={() => navigate("/companies")}>
                <ArrowLeft className="mr-2 w-4 h-4" />
                Späť
              </Button>
              <Button onClick={() => setIsAddingManager(true)}>
                <Plus className="mr-2 w-4 h-4" />
                Pridať manažéra
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {managers.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="mx-auto w-12 h-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">
                Žiadni manažéri
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Začnite pridaním nového manažéra pre vaše skupiny.
              </p>
              <div className="mt-6">
                <Button onClick={() => setIsAddingManager(true)}>
                  <Plus className="mr-2 w-4 h-4" />
                  Pridať manažéra
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meno</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Heslo</TableHead>
                  <TableHead>Riešiteľská skupina</TableHead>
                  <TableHead className="w-[100px]">Akcie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {managers.map((manager) => (
                  <TableRow key={manager.id}>
                    <TableCell className="font-medium">
                      {manager.first_name} {manager.last_name}
                    </TableCell>
                    <TableCell>{manager.email}</TableCell>
                    <TableCell>
                      <code className="px-2 py-1 bg-gray-100 rounded">
                        {manager.id_code}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {manager.facility_managers.map((fm) => (
                          <Badge key={fm.facility_id} variant="outline">
                            {fm.facilities.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{renderManagerActions(manager)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Manager Dialog */}
      <Dialog open={isAddingManager} onOpenChange={setIsAddingManager}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pridať nového manažéra</DialogTitle>
          </DialogHeader>
          <ManagerForm
            facilities={facilities}
            onSubmit={async (formData) => {
              try {
                await createManagerViaAPI(formData);

                toast({
                  title: "Manažér pridaný",
                  description:
                    "Nový manažér bol úspešne pridaný (vrátane Auth účtu)",
                });

                await fetchData();
                setIsAddingManager(false);
              } catch (error) {
                console.error("Error:", error);
                setError("Nepodarilo sa pridať manažéra: " + error.message);
                toast({
                  title: "Chyba",
                  description:
                    "Nepodarilo sa pridať manažéra: " + error.message,
                  variant: "destructive",
                });
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Manager Dialog */}
      <Dialog open={isEditingManager} onOpenChange={setIsEditingManager}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upraviť manažéra</DialogTitle>
          </DialogHeader>
          <ManagerForm
            facilities={facilities}
            initialData={selectedManager}
            onSubmit={async (formData) => {
              try {
                // Kontrola či sa heslo zmenilo
                const passwordChanged =
                  formData.id_code !== selectedManager.id_code;

                if (passwordChanged) {
                  // Ak sa heslo zmenilo, použijeme API endpoint
                  console.log("Manager password changed, updating via API...");
                  await updatePasswordViaAPI(
                    selectedManager.id,
                    formData.id_code
                  );

                  // Aktualizujeme ostatné údaje bez hesla
                  const { error: userError } = await supabase
                    .from("users")
                    .update({
                      first_name: formData.first_name,
                      last_name: formData.last_name,
                      email: formData.email,
                    })
                    .eq("id", selectedManager.id);

                  if (userError) throw userError;
                } else {
                  // Ak sa heslo nezmenilo, štandardný update
                  const { error: userError } = await supabase
                    .from("users")
                    .update({
                      first_name: formData.first_name,
                      last_name: formData.last_name,
                      email: formData.email,
                      id_code: formData.id_code,
                    })
                    .eq("id", selectedManager.id);

                  if (userError) throw userError;
                }

                // Update facility assignments (facility_managers table)
                await supabase
                  .from("facility_managers")
                  .delete()
                  .eq("user_id", selectedManager.id);

                const facilityManagerData = formData.selectedFacilities.map(
                  (facilityId) => ({
                    user_id: selectedManager.id,
                    facility_id: facilityId,
                    is_active: true,
                  })
                );

                const { error: managerError } = await supabase
                  .from("facility_managers")
                  .insert(facilityManagerData);

                if (managerError) throw managerError;

                // Update user_facilities table as well
                await supabase
                  .from("user_facilities")
                  .delete()
                  .eq("user_id", selectedManager.id);

                const userFacilityData = formData.selectedFacilities.map(
                  (facilityId) => ({
                    user_id: selectedManager.id,
                    facility_id: facilityId,
                    is_active: true,
                  })
                );

                const { error: userFacilityError } = await supabase
                  .from("user_facilities")
                  .insert(userFacilityData);

                if (userFacilityError) throw userFacilityError;

                toast({
                  title: "Manažér upravený",
                  description: passwordChanged
                    ? "Údaje manažéra boli úspešne aktualizované vrátane hesla v auth systéme"
                    : "Údaje manažéra boli úspešne aktualizované",
                });

                await fetchData();
                setIsEditingManager(false);
                setSelectedManager(null);
              } catch (error) {
                console.error("Error:", error);
                setError("Nepodarilo sa upraviť manažéra: " + error.message);
                toast({
                  title: "Chyba",
                  description:
                    "Nepodarilo sa upraviť manažéra: " + error.message,
                  variant: "destructive",
                });
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <PasswordChangeDialog
        isOpen={isPasswordChangeDialogOpen}
        onClose={() => {
          setIsPasswordChangeDialogOpen(false);
          setSelectedManagerForPassword(null);
        }}
        onSubmit={handlePasswordChange}
        managerName={
          selectedManagerForPassword
            ? `${selectedManagerForPassword.first_name} ${selectedManagerForPassword.last_name}`
            : ""
        }
      />

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ManagerManagement;
